import type { ComponentPropsWithRef } from 'react';

import { cn } from '@core/utils/cn';

export interface InputProps extends ComponentPropsWithRef<'input'> {
  invalid?: boolean;
}

/**
 * A plain input, styled once.
 *
 * <p>Not wrapped in Base UI's Field.Control: this has to work both inside {@link Field}
 * and on its own (the fleet filter box is not part of a form), and Field.Control outside a
 * Field.Root is a runtime error rather than a graceful degradation.
 *
 * <p>The invalid state is a prop rather than `:invalid`, because the errors that matter
 * here come from the server — a 409 on a device id is not something the browser's
 * constraint validation can know about.
 */
export function Input({ className, invalid, ...props }: InputProps) {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={cn(
        'bg-fill-subtle text-fg h-10 w-full rounded-md px-3 text-[15px]',
        'ring-separator placeholder:text-fg-subtle ring-1 ring-inset',
        'transition-[box-shadow,background-color] duration-150',
        'focus-visible:ring-accent outline-none focus-visible:ring-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        invalid && 'ring-danger focus-visible:ring-danger',
        className,
      )}
      {...props}
    />
  );
}
